import type { KyResponse } from 'ky';

/**
 * Structured error body from API responses.
 */
export interface ApiErrorBody {
    message?: string;
    error?: string;
    code?: string;
    details?: Record<string, unknown>;
}

/**
 * Custom error class for API errors.
 * Provides structured access to HTTP error details.
 */
export class ApiError extends Error {
    public readonly status: number;
    public readonly statusText: string;
    public readonly body: ApiErrorBody | null;
    public readonly response: KyResponse;

    constructor(
        message: string,
        status: number,
        statusText: string,
        body: ApiErrorBody | null,
        response: KyResponse,
    ) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.statusText = statusText;
        this.body = body;
        this.response = response;

        // Maintains proper stack trace for where error was thrown
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiError);
        }
    }

    /**
     * Check if error is a 400 Bad Request.
     */
    isBadRequest(): boolean {
        return this.status === 400;
    }

    /**
     * Check if error is a 401 Unauthorized.
     */
    isUnauthorized(): boolean {
        return this.status === 401;
    }

    /**
     * Check if error is a 403 Forbidden.
     */
    isForbidden(): boolean {
        return this.status === 403;
    }

    /**
     * Check if error is a 404 Not Found.
     */
    isNotFound(): boolean {
        return this.status === 404;
    }

    /**
     * Check if error is a 422 Unprocessable Entity.
     */
    isValidationError(): boolean {
        return this.status === 422;
    }

    /**
     * Check if error is a 429 Too Many Requests.
     */
    isRateLimited(): boolean {
        return this.status === 429;
    }

    /**
     * Check if error is a server error (5xx).
     */
    isServerError(): boolean {
        return this.status >= 500 && this.status < 600;
    }

    /**
     * Check if error is a client error (4xx).
     */
    isClientError(): boolean {
        return this.status >= 400 && this.status < 500;
    }

    /**
     * Get a user-friendly error message.
     */
    getUserMessage(): string {
        if (this.body?.message) {
            return this.body.message;
        }
        if (this.body?.error) {
            return this.body.error;
        }

        switch (this.status) {
        case 400:
            return 'Invalid request. Please check your input.';
        case 401:
            return 'Please sign in to continue.';
        case 403:
            return 'You do not have permission to perform this action.';
        case 404:
            return 'The requested resource was not found.';
        case 422:
            return 'The provided data is invalid.';
        case 429:
            return 'Too many requests. Please try again later.';
        case 500:
            return 'An unexpected error occurred. Please try again.';
        case 502:
        case 503:
        case 504:
            return 'Service temporarily unavailable. Please try again later.';
        default:
            return this.message || 'An error occurred.';
        }
    }
}
